import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Injectable} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {AgendaelementId, AvstemningId, AvstemningMedResultatModell, AvstemningsalternativId, AvstemningsalternativSpesifikasjon, GruppeId, HtmlTekst, LitenIkkeTomStreng, OppdaterAvstemningSpesifikasjon, OpprettAvstemningSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {AvstemningDatatjeneste} from "felles-ng2/tjenester/datalager/AvstemningDatatjeneste";
import {i18n, I18nTekst} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatFormFieldModule} from "@angular/material/form-field";
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {TranslateModule} from "@ngx-translate/core";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {MatTooltipModule} from "@angular/material/tooltip";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {oppdaterGuiVedEndringIFormArrayLengde} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {erTomListe, siste} from "felles-ng2/støttefunksjoner/lister";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {startWith} from "rxjs";
import {RammeInnholdKomponent, RammeMedTittelKomponent} from "web-ng2/delt/komponenter/ramme-med-tittel/ramme-med-tittel";

const debug = initDebugger('RedigerAvstemningDialogKomponent');

export type AvstemningSkjema = ReturnType<typeof RedigerAvstemningDialogKomponent.opprettSkjema>
export type AvstemningSkjemaVerdier = AvstemningSkjema['value']
export type AvstemningAlternativSkjema = ReturnType<typeof RedigerAvstemningDialogKomponent.opprettAlternativSkjemarad>
export type AvstemningAlternativSkjemaVerdier = AvstemningAlternativSkjema['value']

/** False dersom bruker avbrøt dialogen, true ellers */
export type RedigerAvstemningDialogRespons = boolean;

export type DialogParametre = {
    gruppeId: GruppeId;
    /** Avstemningen som redigeres, satt dersom vi redigerer en eksisterende avstemning */
    avstemning?: AvstemningMedResultatModell;
    /** Iden til saken avstemningen skal opprettes under, satt dersom vi oppretter en ny avstemning */
    sakId?: AgendaelementId;
};

@Component({
    selector: 'rediger-avstemning-dialog',
    templateUrl: 'rediger-avstemning-dialog.html',
    styleUrl: 'rediger-avstemning-dialog.scss',
    standalone: true,
    imports: [
        MatToolbarModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatFormFieldModule,
        MatDialogContent,
        MatDialogActions,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        VisSpinnerDirektiv,
        MatTooltipModule,
        StandardValideringsfeilPipe,
        RiktekstRedigeringKomponent,
        RammeMedTittelKomponent,
        RammeInnholdKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RedigerAvstemningDialogKomponent {
    static visOpprettNy(dialog: MatDialog, sakId: AgendaelementId, gruppeId: GruppeId): MatDialogRef<RedigerAvstemningDialogKomponent, RedigerAvstemningDialogRespons> {
        return dialog.open(RedigerAvstemningDialogKomponent, {
            data: {gruppeId, sakId},
            disableClose: true,
            ariaLabelledBy: 'rediger-avstemning-dialog-tittel',
            minWidth: '50%'
        });
    }

    static visRediger(dialog: MatDialog, avstemning: AvstemningMedResultatModell, gruppeId: GruppeId): MatDialogRef<RedigerAvstemningDialogKomponent, RedigerAvstemningDialogRespons> {
        return dialog.open(RedigerAvstemningDialogKomponent, {
            data: {avstemning, gruppeId},
            disableClose: true,
            ariaLabelledBy: 'rediger-avstemning-dialog-tittel',
            minWidth: '50%'
        });
    }

    static opprettSkjema() {
        return new FormGroup({
            id: new FormControl(nyUuid(), {nonNullable: true}), //ikke eksponert for redigering
            tittel: new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
            beskrivelse: new FormControl<HtmlTekst | null>(null, {validators: FeltValidatorer.htmlTekst}),
            alternativer: new FormArray<AvstemningAlternativSkjema>([], {validators: FeltValidatorer.listeMedMinimumslengde(2)}) //forventer minst to innslag - da siste innslag alltid er template-raden
        });
    }

    static opprettAlternativSkjemarad(verdier = {id: nyUuid(), navn: ''}) {
        return new FormGroup({
            id: new FormControl<AvstemningsalternativId>(verdier.id, {nonNullable: true}),
            tittel: new FormControl<LitenIkkeTomStreng>(verdier.navn, {validators: FeltValidatorer.litenStreng}) //legger ikke til noen 'påkrevd' validering - tomme rader blir ganske enkelt utelatt fra spesifikasjonen
        });
    }

    skjema = RedigerAvstemningDialogKomponent.opprettSkjema();
    modus: 'ny' | 'rediger';
    gruppeId: GruppeId;
    /** sak avstemning skal opprettes under, kun satt ved opprettelse av ny avstemning */
    sakId: AgendaelementId | null = null;
    // modell: AvstemningskjemaModell;
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<RedigerAvstemningDialogKomponent, RedigerAvstemningDialogRespons>,
                private avhengigheter: RedigerAvstemningDialogKomponentAvhengigheter,
                private meldingTilBruker: MeldingTilBrukerNg2,
                cdRef: ChangeDetectorRef) {
        this.gruppeId = parametre.gruppeId;
        if (erIkkeNil(parametre.sakId)) {
            this.modus = 'ny';
            this.sakId = parametre.sakId;
            this.skjema = RedigerAvstemningDialogKomponent.opprettSkjema();

        } else {
            this.modus = 'rediger';
            this.skjema = RedigerAvstemningDialogKomponent.opprettSkjema();
            const avstemning = parametre.avstemning!;
            avstemning.avstemningsalternativer.forEach(alternativ => this.leggTilTomRad());
            this.skjema.patchValue({
                id: avstemning.id,
                tittel: avstemning.tittel,
                beskrivelse: avstemning.beskrivelse,
                alternativer: avstemning.avstemningsalternativer.map(({id, tittel}) => ({id, tittel}))
            });
        }

        oppdaterGuiVedEndringIFormArrayLengde(this.skjema.controls.alternativer, cdRef);

        //reager på endringer i skjema
        this.skjema.controls.alternativer.valueChanges.pipe(
          startWith(this.skjema.value.alternativer),
          takeUntilDestroyed())
          .subscribe(rader => {
              //legg til ny rad dersom siste rad er ikke-tom
              if (erTomListe(rader) || erIkkeTomStreng(siste(rader!)!.tittel))
                  this.leggTilTomRad();
          })
    }

    slettAlternativt(radnummer: number) {
        this.skjema.controls.alternativer.removeAt(radnummer);
    }

    async lagre() {
        await this.lagrer.utfør(async () => {
            if (this.modus === 'rediger') {
                const respons = await this.avhengigheter.oppdater(this.skjema.value.id!, this.somOppdaterAvstemningSpesifikasjon());
                this.meldingTilBruker.infomelding(new I18nTekst(i18n('Avstemning har blitt oppdatert')));
                // this.modelloppdateringer.broadcastLokalModellOppdatert(ModelloppdateringType.AVSTEMNING_OPPDATERT, {avstemningId: respons.data!.id, sakId: respons.data!.sakId});
                this.dialogRef.close(true);
            } else {
                const respons = await this.avhengigheter.opprett(this.somOpprettAvstemningSpesifikasjon());
                this.meldingTilBruker.infomelding(new I18nTekst(i18n('Avstemning registrert')));
                // this.modelloppdateringer.broadcastLokalModellOppdatert(ModelloppdateringType.NY_AVSTEMNING, {avstemningId: respons.data!.id, sakId: respons.data!.sakId});
                this.dialogRef.close(true);
            }
        });
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    // private hjelpemetoder

    private leggTilTomRad() {
        this.skjema.controls.alternativer.push(RedigerAvstemningDialogKomponent.opprettAlternativSkjemarad());
    }

    private somOpprettAvstemningSpesifikasjon(): OpprettAvstemningSpesifikasjon {
        const verdier = this.skjema.value;
        return {
            id: verdier.id!,
            sakId: this.sakId!,
            tittel: verdier.tittel!,
            beskrivelse: verdier.beskrivelse ?? null,
            alternativer: this.ikkeBlankeAlternativer()
        };
    }

    private somOppdaterAvstemningSpesifikasjon(): OppdaterAvstemningSpesifikasjon {
        const verdier = this.skjema.value;
        return {
            tittel: verdier.tittel!,
            beskrivelse: verdier.beskrivelse ?? null,
            alternativer: this.ikkeBlankeAlternativer()
        };
    }

    private ikkeBlankeAlternativer(): AvstemningsalternativSpesifikasjon[] {
        return this.skjema.value.alternativer!
          .filter(alternativ => erIkkeTomStreng(alternativ.tittel))
          .map(alternativ => ({id: alternativ.id!, tittel: alternativ.tittel!}));
    }
}

@Injectable()
export class RedigerAvstemningDialogKomponentAvhengigheter {

    constructor(private avstemningDatatjeneste: AvstemningDatatjeneste) {
    }

    async oppdater(id: AvstemningId, spesifikasjon: OppdaterAvstemningSpesifikasjon): Promise<void> {
        await this.avstemningDatatjeneste.oppdater(id, spesifikasjon);
    }

    async opprett(spesifikasjon: OpprettAvstemningSpesifikasjon): Promise<void> {
        await this.avstemningDatatjeneste.opprett(spesifikasjon);
    }
}
